import React from 'react';
import ReactDOM from 'react-dom';
import { Button } from '@components/atoms/Button';
import { formatForDisplay } from '@utils/formatDate';

export interface SubtaskOutOfRangeData {
  subtaskId: string;
  subtaskName: string;
  parentTaskId: string;
  parentTaskName: string;
  parentStartDate: string;
  parentEndDate: string;
  subtaskNewStartDate: string;
  subtaskNewEndDate: string;
  conflictType: 'before_start' | 'after_end' | 'both';
}

interface SubtaskOutOfRangeModalProps {
  isOpen: boolean;
  data: SubtaskOutOfRangeData | null;
  mode: 'move' | 'resize';
  onExtendParent: () => void;
  onLeaveOutside: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const SubtaskOutOfRangeModal: React.FC<SubtaskOutOfRangeModalProps> = ({
  isOpen,
  data,
  mode,
  onExtendParent,
  onLeaveOutside,
  onCancel,
  isLoading = false,
}) => {
  if (!isOpen || !data) return null;

  const isMove = mode === 'move';

  const getConflictDescription = () => {
    if (data.conflictType === 'before_start') {
      return `starts before the parent task's start date`;
    } else if (data.conflictType === 'after_end') {
      return `ends after the parent task's end date`;
    } else {
      return `falls completely outside the parent task's date range`;
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      style={{ zIndex: 99999 }}>
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto relative">
        {/* Header */}
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0">
            <svg
              className="h-6 w-6 text-amber-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Subtask Outside Parent Range
            </h3>
          </div>
        </div>

        {/* Description */}
        <div className="mb-6">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            {isMove ? 'Moving' : 'Resizing'}{' '}
            <strong>"{data.subtaskName}"</strong> {getConflictDescription()}.
          </p>

          {/* Date Comparison */}
          <div className="space-y-3 mb-4">
            {/* Parent Task Dates */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                Parent Task: "{data.parentTaskName}"
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-blue-700 dark:text-blue-300 font-medium">
                    Start:
                  </span>{' '}
                  <span className="text-gray-600 dark:text-gray-300">
                    {formatForDisplay(data.parentStartDate)}
                  </span>
                </div>
                <div>
                  <span className="text-blue-700 dark:text-blue-300 font-medium">
                    End:
                  </span>{' '}
                  <span className="text-gray-600 dark:text-gray-300">
                    {formatForDisplay(data.parentEndDate)}
                  </span>
                </div>
              </div>
            </div>

            {/* Subtask New Dates */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <h4 className="text-sm font-medium text-amber-900 dark:text-amber-100 mb-2">
                Subtask New Dates:
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-amber-700 dark:text-amber-300 font-medium">
                    Start:
                  </span>{' '}
                  <span
                    className={`${
                      data.conflictType === 'before_start' ||
                      data.conflictType === 'both'
                        ? 'text-red-600 dark:text-red-400 font-medium'
                        : 'text-gray-600 dark:text-gray-300'
                    }`}>
                    {formatForDisplay(data.subtaskNewStartDate)}
                    {(data.conflictType === 'before_start' ||
                      data.conflictType === 'both') &&
                      ' (before parent)'}
                  </span>
                </div>
                <div>
                  <span className="text-amber-700 dark:text-amber-300 font-medium">
                    End:
                  </span>{' '}
                  <span
                    className={`${
                      data.conflictType === 'after_end' ||
                      data.conflictType === 'both'
                        ? 'text-red-600 dark:text-red-400 font-medium'
                        : 'text-gray-600 dark:text-gray-300'
                    }`}>
                    {formatForDisplay(data.subtaskNewEndDate)}
                    {(data.conflictType === 'after_end' ||
                      data.conflictType === 'both') &&
                      ' (after parent)'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Options */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              What would you like to do?
            </h4>

            {/* Option 1: Extend Parent */}
            <button type="button"
              onClick={onExtendParent}
              disabled={isLoading}
              className="w-full text-left bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <div className="flex items-start">
                <svg
                  className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                  />
                </svg>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">
                    EXTEND PARENT TASK DATES
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-300 mt-1">
                    {data.conflictType === 'before_start' &&
                      `The parent task "${data.parentTaskName}" will start on ${formatForDisplay(data.subtaskNewStartDate)} to include the subtask.`}
                    {data.conflictType === 'after_end' &&
                      `The parent task "${data.parentTaskName}" will end on ${formatForDisplay(data.subtaskNewEndDate)} to include the subtask.`}
                    {data.conflictType === 'both' &&
                      `The parent task "${data.parentTaskName}" dates will be adjusted to include the subtask.`}
                  </p>
                </div>
              </div>
            </button>

            {/* Option 2: Leave Outside */}
            <button type="button"
              onClick={onLeaveOutside}
              disabled={isLoading}
              className="w-full text-left bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <div className="flex items-start">
                <svg
                  className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
                <div className="ml-3">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    ALLOW SUBTASK OUTSIDE PARENT RANGE
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-300 mt-1">
                    {isMove ? 'Move' : 'Resize'} the subtask to the new dates.
                    It will be outside the parent task's date range.
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Cancel Button */}
        <div className="flex justify-end">
          <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        </div>

        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 dark:bg-gray-800 dark:bg-opacity-75 flex items-center justify-center rounded-lg">
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-indigo-600 border-t-transparent"></div>
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Updating tasks...
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};
